

import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import SignIn from './SignIn';
import ErrorBoundary from './ErrorBoundary';
import AppLayout from './layouts/AppLayout';

const MainMenu = lazy(() => import('./MainMenu'));
const AdminMenu = lazy(() => import('./AdminMenu'));
const OPD = lazy(() => import('./OPD'));
const IndoorPage = lazy(() => import('./components/indoor/IndoorPage'));
const PathologyPage = lazy(() => import('./pathology/PathologyPage'));
const OperationTheatrePage = lazy(() => import('./components/operation/OperationTheatrePage'));
const EmergencyPage = lazy(() => import('./components/emergency/EmergencyPage'));
const ERProtocolsPage = lazy(() => import('./components/emergency/ERProtocolsPage'));
const RadiologyPage = lazy(() => import('./radiology/RadiologyPage'));
const AzadPage = lazy(() => import('./AzadPage'));
const PatientRecordPage = lazy(() => import('./components/patientRecord/PatientRecordPage'));
const MyPortal = lazy(() => import('./components/doctor/MyPortal'));
const UserProfile = lazy(() => import('./components/UserProfile'));
const DoctorsList = lazy(() => import('./DoctorsList'));
const DoctorViewModal = lazy(() => import('./components/doctor/DoctorViewModal'));
const DoctorProfileForm = lazy(() => import('./components/doctor/DoctorProfileForm'));
const PharmacyApp = lazy(() => import('./components/pharmacy/PharmacyApp'));
const RightSideCashReconciliation = lazy(() => import('./components/dashboard/RightSideCashReconciliation'));
const SettingsHubPage = lazy(() => import('./components/admin/SettingsHubPage'));

const RESTOREABLE_PAGES = new Set(['main', 'opd', 'ipd', 'pathology', 'radiology', 'pharmacy', 'admin', 'azad', 'patient_record', 'my_portal', 'doctors-list', 'register-doctor', 'operation_theatre', 'emergency']);
const INACTIVITY_TIMEOUT_MS = 1000 * 60 * 60 * 12; // 12 hours
const MODULE_PAGES = new Set(['opd', 'ipd', 'pathology', 'radiology', 'pharmacy', 'admin', 'patient_record', 'operation_theatre', 'emergency']);
const KNOWN_MODULE_CODES = new Set(['opd', 'ipd', 'pathology', 'radiology', 'pharmacy', 'admin', 'patient_record', 'operation_theatre', 'emergency', 'blood_bank', 'hdu_icu', 'dentistry']);

function canonicalIdentity(value) {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return '';
  const at = v.indexOf('@');
  if (at === -1) return v.replace(/[._\-\s]+/g, '');
  const local = v.slice(0, at).replace(/[._\-\s]+/g, '');
  const domain = v.slice(at + 1);
  return `${local}@${domain}`;
}

function isPrivilegedUser(user) {
  const role = String(user && (user.role || user.roles) ? (user.role || user.roles) : '').toLowerCase().replace(/[^a-z0-9]+/g, '_');
  if (user?.is_superadmin === true || user?.isSuperAdmin === true) return true;
  if (Number(user?.is_superadmin) === 1 || Number(user?.isSuperAdmin) === 1) return true;
  return role === 'superadmin' || role === 'super_admin' || role.includes('superadmin') || role.includes('super_admin');
}

function normalizeModuleCode(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (!s) return null;
  if (s.includes('patholog') || s.includes('lab')) return 'pathology';
  if (s.includes('pharmac')) return 'pharmacy';
  if (s.includes('opd') || s.includes('out door') || s.includes('outdoor')) return 'opd';
  if (s.includes('ipd') || s.includes('in door') || s.includes('indoor')) return 'ipd';
  if (s.includes('patient') && s.includes('record')) return 'patient_record';
  if (s.includes('radiolog') || s.includes('imaging')) return 'radiology';
  if (s.includes('blood')) return 'blood_bank';
  if (s.includes('operation') || s.includes('theatre') || s.includes('ot')) return 'operation_theatre';
  if (s.includes('icu') || s.includes('hdu')) return 'hdu_icu';
  if (s.includes('dental') || s.includes('dentistry')) return 'dentistry';
  if (s.includes('admin')) return 'admin';
  return s.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

function canAccessModule(key) {
  try {
    const user = JSON.parse(localStorage.getItem('hmis_user') || '{}');
    if (isPrivilegedUser(user)) return true;
    const mods = getAssignedModules(user);
    if (!mods.length) return true; // no explicit restriction = full access
    return mods.includes(key);
  } catch (e) {
    return true;
  }
}

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('hmis_user') || '{}');
  } catch (e) {
    return {};
  }
}

function getAssignedModules(user) {
  if (!user || isPrivilegedUser(user)) return [];
  let rawModules = Array.isArray(user.modules) ? user.modules : [];
  if (!rawModules.length && user.modules_access) {
    try {
      if (Array.isArray(user.modules_access)) rawModules = user.modules_access;
      else {
        const raw = String(user.modules_access || '').trim();
        rawModules = raw ? JSON.parse(raw) : [];
      }
    } catch (e) {
      const raw = String(user.modules_access || '').trim();
      rawModules = raw ? raw.split(/[,;|]\s*/) : [];
    }
  }
  const pickModuleValue = (item) => {
    if (typeof item === 'string' || typeof item === 'number') return item;
    if (!item || typeof item !== 'object') return null;
    return (
      item.code
      || item.module_code
      || item.module_name
      || item.module_key
      || item.name
      || item.key
      || (item.module && (item.module.code || item.module.module_code || item.module.module_name || item.module.module_key || item.module.name || item.module.key))
    );
  };
  const normalized = Array.from(new Set(rawModules.map((item) => {
    return normalizeModuleCode(pickModuleValue(item));
  }).filter(Boolean)));
  // Ignore unknown/non-canonical values (for example numeric module ids) to avoid false access denials.
  return normalized.filter((code) => KNOWN_MODULE_CODES.has(code));
}

function hasDashboardAccess(user) {
  return true;
}

function getDefaultModulePage(user) {
  const mods = getAssignedModules(user);
  return mods.length === 1 ? mods[0] : 'main';
}

function resolveRequestedPage(user, requestedPage) {
  const page = requestedPage || 'main';
  const assignedModules = getAssignedModules(user);
  const hasModuleAccess = (key) => {
    if (isPrivilegedUser(user)) return true;
    if (!assignedModules.length) return true;
    return assignedModules.includes(key);
  };
  if (page === 'login' || page === 'profile' || page === 'change-password' || page === 'my_portal' || page === 'doctors-list' || page === 'register-doctor') {
    return page;
  }
  if (page === 'main' && !hasDashboardAccess(user)) {
    return getDefaultModulePage(user);
  }
  if (MODULE_PAGES.has(page) && !hasModuleAccess(page)) {
    return hasDashboardAccess(user) ? 'main' : getDefaultModulePage(user);
  }
  return page;
}

function getInitialPage() {
  try {
    const rawUser = localStorage.getItem('hmis_user');
    const token = localStorage.getItem('hmis_token');
    if (!rawUser || !token) return 'login';
    const lastPage = localStorage.getItem('hmis_last_page');
    if (lastPage && RESTOREABLE_PAGES.has(lastPage)) return lastPage;
    return 'main';
  } catch (e) {
    return 'login';
  }
}


function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(getInitialPage);
  const [showDoctorForm, setShowDoctorForm] = useState(false);
  const [viewDoctor, setViewDoctor] = useState(null);
  const [editDoctor, setEditDoctor] = useState(null);
  const [deleteDoctor, setDeleteDoctor] = useState(null);
  const [doctorsListKey, setDoctorsListKey] = useState(0); // for refresh
  const currentUser = readStoredUser();
  const locationPage = String(location.pathname.replace(/^\/+/, '') || 'main');
  const isRootPath = location.pathname === '/';
  const requestedPage = isRootPath ? page : locationPage;
  const activePage = resolveRequestedPage(currentUser, requestedPage || locationPage);
  const canOpenDashboard = hasDashboardAccess(currentUser);
  const hasSession = (() => {
    try {
      return !!localStorage.getItem('hmis_user') && !!localStorage.getItem('hmis_token');
    } catch (e) {
      return false;
    }
  })();
  const navigateToPage = (targetPage) => {
    const nextPage = resolveRequestedPage(readStoredUser(), targetPage);
    const nextPath = nextPage === 'login' ? '/login' : `/${nextPage}`;
    navigate(nextPath, { replace: true });
  };

  useEffect(() => {
    const nextPage = resolveRequestedPage(currentUser, locationPage || 'main');
    if (page !== nextPage) {
      setPage(nextPage);
    }
  }, [currentUser, locationPage, page]);

  useEffect(() => {
    try {
      const assigned = getAssignedModules(readStoredUser());
      if (assigned.length === 1) localStorage.setItem('hmis_single_module', '1');
      else localStorage.removeItem('hmis_single_module');
    } catch (e) {}
  }, [activePage]);

  useEffect(() => {
    let mounted = true;
    const syncModules = async () => {
      try {
        const token = localStorage.getItem('hmis_token');
        const current = readStoredUser();
        if (!token || !current || !current.id) return;
        const tenantId = current?.tenant_id || current?.tenantId || null;
        const res = await fetch('/api/tenant/modules' + (tenantId ? ('?tenantId=' + encodeURIComponent(tenantId)) : ''), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => []);
        const codes = Array.isArray(data)
          ? data.map((item) => {
              if (typeof item === 'string' || typeof item === 'number') return normalizeModuleCode(item);
              const rawModule = item && (
                item.code
                || item.module_code
                || item.module_name
                || item.module_key
                || item.name
                || item.key
                || (item.module && (item.module.code || item.module.module_code || item.module.module_name || item.module.module_key || item.module.name || item.module.key))
              );
              return normalizeModuleCode(rawModule);
            }).filter(Boolean)
          : [];
        if (!mounted || !codes.length) return;
        const existing = getAssignedModules(current);
        if (JSON.stringify(existing) === JSON.stringify(codes)) return;
        const nextUser = { ...current, modules: codes };
        localStorage.setItem('hmis_user', JSON.stringify(nextUser));
        setPage((prev) => resolveRequestedPage(nextUser, prev));
      } catch (e) {}
    };
    syncModules();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const targetPath = activePage === 'login' ? '/login' : `/${activePage}`;
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  }, [activePage, location.pathname, navigate]);

  // Persist active page so refresh restores to the same module when session exists.
  useEffect(() => {
    try {
      if (activePage !== 'login' && RESTOREABLE_PAGES.has(activePage)) {
        localStorage.setItem('hmis_last_page', activePage);
      }
    } catch (e) {}
  }, [activePage]);

  // Global Enter-key behavior: move focus to next field, or submit the nearest form
  useEffect(() => {
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const onKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      const active = document.activeElement;
      if (!active) return;
      const tag = (active.tagName || '').toLowerCase();
      if (tag === 'textarea' || active.isContentEditable) return; // allow newline in textareas

      // If inside a form, prefer submitting the form via its submit button
      const form = active.closest && active.closest('form');
      if (form) {
        const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
        if (submitBtn) {
          e.preventDefault();
          submitBtn.click();
          return;
        }
        // no submit button: move focus to next focusable inside the form
        const list = Array.from(form.querySelectorAll(focusableSelector)).filter(el => el.offsetParent !== null);
        const idx = list.indexOf(active);
        const next = list[idx + 1] || list[0];
        if (next) { e.preventDefault(); next.focus(); }
        return;
      }

      // Not in a form: move to next focusable in document
      const all = Array.from(document.querySelectorAll(focusableSelector)).filter(el => el.offsetParent !== null);
      const i = all.indexOf(active);
      const next = all[i + 1] || all[0];
      if (next) {
        e.preventDefault();
        // if next is a submit/primary button, click it
        const ntag = (next.tagName || '').toLowerCase();
        if (ntag === 'button' || ntag === 'input') {
          const type = (next.getAttribute('type') || '').toLowerCase();
          if (type === 'submit' || next.dataset && next.dataset.primary === 'true') {
            next.click();
            return;
          }
        }
        next.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // expose a global navigation helper so header/profile menu can navigate
  useEffect(() => {
    try { window.appNavigate = (p) => navigateToPage(p); } catch (e) {}
    return () => { try { window.appNavigate = null; } catch (e) {} };
  }, []);

  // Listen for event-based navigation as a robust fallback
  useEffect(() => {
    const handler = (e) => {
      try {
        const p = (e && e.detail && e.detail.page) || null;
        if (p) navigateToPage(p);
      } catch (err) {}
    };
    window.addEventListener('hmis:navigate', handler);
    return () => window.removeEventListener('hmis:navigate', handler);
  }, []);

  // Global profile/password shortcuts from sidebar user cards.
  useEffect(() => {
    const openProfile = () => navigateToPage('profile');
    const openPassword = () => navigateToPage('change-password');
    window.addEventListener('hmis:openProfileMenu', openProfile);
    window.addEventListener('hmis:openChangePassword', openPassword);
    return () => {
      window.removeEventListener('hmis:openProfileMenu', openProfile);
      window.removeEventListener('hmis:openChangePassword', openPassword);
    };
  }, []);

  // Auto sign-out after inactivity timeout while keeping refresh persistence for active sessions.
  useEffect(() => {
    const hasSession = (() => {
      try { return !!localStorage.getItem('hmis_user'); } catch (e) { return false; }
    })();
    if (!hasSession) return;

    const now = Date.now();
    const readLast = () => {
      try {
        const v = Number(localStorage.getItem('hmis_session_last_activity') || '0');
        return Number.isFinite(v) ? v : 0;
      } catch (e) {
        return 0;
      }
    };
    const writeLast = (ts) => {
      try { localStorage.setItem('hmis_session_last_activity', String(ts)); } catch (e) {}
    };

    const existing = readLast();
    if (!existing) writeLast(now);

    let lastWrite = now;
    const onActivity = () => {
      const t = Date.now();
      // throttle writes to reduce storage churn
      if (t - lastWrite > 15000) {
        lastWrite = t;
        writeLast(t);
      }
    };

    const checkIdle = () => {
      const last = readLast() || Date.now();
      if (Date.now() - last >= INACTIVITY_TIMEOUT_MS) {
        try {
          localStorage.removeItem('hmis_user');
          localStorage.removeItem('hmis_token');
          localStorage.removeItem('hmis_last_page');
          localStorage.removeItem('hmis_session_last_activity');
        } catch (e) {}
        setPage('login');
      }
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    const timer = setInterval(checkIdle, 60000);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearInterval(timer);
    };
  }, [page]);

  // Protect app pages behind auth: if a session is missing, force login screen.
  useEffect(() => {
    if (activePage === 'login') return;
    const hasSession = (() => {
      try {
        // Accept session with hmis_user alone (token may not be set for legacy sessions)
        return !!localStorage.getItem('hmis_user');
      } catch (e) {
        return false;
      }
    })();
    if (!hasSession) {
      try {
        localStorage.removeItem('hmis_last_page');
        localStorage.removeItem('hmis_session_last_activity');
      } catch (e) {}
      navigateToPage('login');
    }
  }, [activePage]);

  const moduleFallback = (
    <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#546e7a', fontSize: 14 }}>
      Loading module...
    </div>
  );
  const renderLazy = (node, boundaryKey = activePage) => (
    <ErrorBoundary key={boundaryKey}>
      <Suspense fallback={moduleFallback}>{node}</Suspense>
    </ErrorBoundary>
  );

  return (
    <Routes>
      <Route path="/login" element={<SignIn onLogin={(u, targetPage) => {
        const allowedTargets = new Set(['main', 'opd', 'ipd', 'pathology', 'pharmacy', 'admin', 'azad', 'patient_record', 'my_portal', 'operation_theatre', 'emergency']);
        let nextPage = allowedTargets.has(targetPage) ? targetPage : 'main';
        if (nextPage === 'azad' && !isPrivilegedUser(u || {})) {
          nextPage = 'main';
        }
        nextPage = resolveRequestedPage(u || {}, nextPage);
        try {
          localStorage.setItem('hmis_last_page', nextPage);
          localStorage.setItem('hmis_session_last_activity', String(Date.now()));
        } catch (e) {}
        navigate(nextPage === 'login' ? '/login' : `/${nextPage}`, { replace: true });
      }} />} />
      <Route path="/main" element={hasSession ? <AppLayout /> : <Navigate to="/login" replace />}>
        <Route index element={hasSession ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : <Navigate to="/login" replace />} />
      </Route>
      <Route path="/azad" element={!hasSession ? <Navigate to="/login" replace /> : (isPrivilegedUser(readStoredUser() || {}) ? renderLazy(<AzadPage onHome={() => navigateToPage('main')} />) : <Navigate to="/main" replace />)} />
      <Route element={<AppLayout />}>
        <Route path="/profile" element={renderLazy(<UserProfile mode="profile" user={readStoredUser()} onBack={() => navigateToPage(canOpenDashboard ? 'main' : getDefaultModulePage(readStoredUser()))} onLogout={() => {
          localStorage.removeItem('hmis_user');
          localStorage.removeItem('hmis_token');
          localStorage.removeItem('hmis_last_page');
          localStorage.removeItem('hmis_session_last_activity');
          navigate('/login', { replace: true });
        }} />)} />
        <Route path="/change-password" element={renderLazy(<UserProfile mode="change-password" user={readStoredUser()} onBack={() => navigateToPage(canOpenDashboard ? 'main' : getDefaultModulePage(readStoredUser()))} onLogout={() => {
          localStorage.removeItem('hmis_user');
          localStorage.removeItem('hmis_token');
          localStorage.removeItem('hmis_last_page');
          localStorage.removeItem('hmis_session_last_activity');
          navigate('/login', { replace: true });
        }} />)} />
        <Route path="/dashboard" element={hasSession ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : <Navigate to="/login" replace />} />
        <Route path="/opd/*" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('opd') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<OPD onHomeClick={canOpenDashboard ? () => navigateToPage('main') : undefined} />))} />
        <Route path="/ipd/*" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('ipd') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<IndoorPage onHome={canOpenDashboard ? () => navigateToPage('main') : undefined} />))} />
        <Route path="/pathology/*" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('pathology') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<PathologyPage onHome={canOpenDashboard ? () => navigateToPage('main') : undefined} />))} />
        <Route path="/radiology/*" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('radiology') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<RadiologyPage onHome={canOpenDashboard ? () => navigateToPage('main') : undefined} />))} />
        <Route path="/operation_theatre/*" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('operation_theatre') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<OperationTheatrePage onHome={canOpenDashboard ? () => navigateToPage('main') : undefined} />))} />
        <Route path="/emergency/er-protocols" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('emergency') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<ERProtocolsPage />))} />
        <Route path="/emergency/*" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('emergency') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<EmergencyPage onHome={canOpenDashboard ? () => navigateToPage('main') : undefined} />))} />
        <Route path="/pharmacy/*" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('pharmacy') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<PharmacyApp allowDashboardAccess={canOpenDashboard} embeddedInShell />))} />
        <Route path="/patient_record/*" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('patient_record') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<PatientRecordPage onHome={canOpenDashboard ? () => navigateToPage('main') : undefined} />))} />
        <Route path="/cash-reconciliation" element={!hasSession ? <Navigate to="/login" replace /> : renderLazy(<RightSideCashReconciliation visible />)} />
        <Route path="/settings" element={!hasSession ? <Navigate to="/login" replace /> : renderLazy(<SettingsHubPage />)} />
        <Route path="/my_portal" element={hasSession ? renderLazy(<MyPortal onHome={canOpenDashboard ? () => navigateToPage('main') : undefined} />) : <Navigate to="/login" replace />} />
        <Route path="/admin/*" element={!hasSession ? <Navigate to="/login" replace /> : (!canAccessModule('admin') ? renderLazy(<MainMenu onTileClick={navigateToPage} />) : renderLazy(<AdminMenu onTileClick={navigateToPage} />))} />
      </Route>
      <Route element={<AppLayout />}>
        <Route path="/doctors-list" element={renderLazy(<>
        <DoctorsList 
          key={doctorsListKey}
          onRegisterClick={e => {
            e.stopPropagation();
            setShowDoctorForm(true);
          }}
          onView={doc => setViewDoctor(doc)}
          onEdit={doc => setEditDoctor(doc)}
          onDelete={doc => setDeleteDoctor(doc)}
        />
        {deleteDoctor && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.25)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => setDeleteDoctor(null)}
          >
            <div 
              style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 32px rgba(0,0,0,0.18)', minWidth: 340, padding: 32, position: 'relative' }}
              onClick={e => e.stopPropagation()}
            >
              <h3>Delete Doctor</h3>
              <p>Are you sure you want to delete <b>{deleteDoctor.name}</b>?</p>
              <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
                <button style={{ background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}
                  onClick={async () => {
                    try {
                      await fetch(`/api/doctors/${deleteDoctor.doctor_id || deleteDoctor.id}`, { method: 'DELETE' });
                      setDeleteDoctor(null);
                      setDoctorsListKey(k => k + 1);
                    } catch {
                      alert('Failed to delete doctor');
                    }
                  }}
                >Delete</button>
                <button style={{ background: '#eee', color: '#333', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => setDeleteDoctor(null)}
                >Cancel</button>
              </div>
            </div>
          </div>
        )}
        {viewDoctor && (
          <DoctorViewModal doctor={viewDoctor} onClose={() => setViewDoctor(null)} />
        )}
      </>)} />
        <Route path="/register-doctor" element={renderLazy(<DoctorProfileForm onClose={() => navigate('/doctors-list', { replace: true })} />)} />
      </Route>
      <Route path="/" element={<Navigate to={activePage === 'login' ? '/login' : `/${activePage}`} replace />} />
      <Route path="*" element={<Navigate to={activePage === 'login' ? '/login' : `/${activePage}`} replace />} />
    </Routes>
  );
}

export default App;
